import { useState, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '../../../../components/ui/button';
import type { PostMedia, PostPlatform } from '../../../../types/publishing';
import { PortalDialog } from './PortalDialog';
import { socialApi } from './socialApi';

// The AI caption workspace, as a dialog opened from the Caption section, with the post
// PREVIEW beside it so a draft is judged against how it will look.
//
// ⚠️⚠️ ON-DEMAND ONLY, ABSOLUTELY. Opening this dialog calls NOTHING: there is no effect
// and no query here, and the only calls are the Draft and Revise buttons' own click
// handlers (mutate). A dialog that fetched on mount would bill every club that merely
// looked, and it would read as a feature rather than a bug. socialOnDemandOnly.test.ts
// watches this directory.
//
// ⚠️ THE DIALOG EDITS A WORKING COPY. "Use this caption" is the ONLY thing that writes the
// composer's caption, and it writes the BASE caption only (Run K section 1): a different
// caption a club set for one platform stays exactly as typed. Cancel, Close and Escape
// leave the composer untouched, including after a Revise, which acts on the working copy.

function errorText(err: unknown): string {
  return err instanceof Error ? err.message : 'Something went wrong. Try again.';
}

export function CaptionWorkspaceDialog({
  accountId,
  platforms,
  media,
  caption,
  brief,
  onBrief,
  tone,
  onTone,
  preview,
  onAccept,
  onClose,
}: {
  accountId: string;
  platforms: PostPlatform[];
  media: PostMedia[];
  /** The composer's current base caption: where the working copy starts. */
  caption: string;
  brief: string;
  onBrief: (v: string) => void;
  tone: string;
  onTone: (v: string) => void;
  /** The post preview for a given base caption, so the dialog shows the draft as it will look. */
  preview: (caption: string) => ReactNode;
  onAccept: (caption: string) => void;
  onClose: () => void;
}) {
  const [working, setWorking] = useState(caption);
  const [drafts, setDrafts] = useState<Array<{ style: string; caption: string }>>([]);
  const [instruction, setInstruction] = useState('');

  const ask = () => ({ kyckAccountId: accountId, destinations: platforms, brief, tone, emoji: 'light' as const, media });
  const draft = useMutation({
    mutationFn: () => socialApi.draftCaption(ask()),
    onSuccess: (r) => setDrafts(r.drafts),
  });
  const refine = useMutation({
    mutationFn: () => socialApi.refineCaption({ ...ask(), currentDraft: working, instruction }),
    onSuccess: (r) => setWorking(r.draft),
  });

  return (
    <PortalDialog title="Write the caption with AI" onClose={onClose} wide testId="caption-workspace">
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="min-w-0 space-y-3">
          <p className="text-xs text-muted-foreground">
            AI drafts are suggestions. Read every word: you are responsible for what you publish, and facts, prices and offers must be checked.
          </p>
          <p className="text-xs text-muted-foreground" data-testid="ai-base-only">
            AI writes and revises the main caption only. A different caption you have set for one platform stays exactly as you typed it.
          </p>
          <label className="block text-sm">
            What is the post about?
            <textarea className="mt-1 w-full rounded-md border border-input bg-background p-2 text-sm" rows={3} value={brief} onChange={(e) => onBrief(e.target.value)} />
          </label>
          <label className="block text-sm">
            Tone (optional)
            <input className="mt-1 w-full rounded-md border border-input bg-background p-2 text-sm" value={tone} onChange={(e) => onTone(e.target.value)} />
          </label>
          <div>
            <Button type="button" variant="outline" disabled={draft.isPending || platforms.length === 0} onClick={() => draft.mutate()}>
              {draft.isPending ? 'Writing... this can take up to a minute' : 'Draft captions'}
            </Button>
            {platforms.length === 0 && <span className="ml-2 text-xs text-muted-foreground">Pick a platform first.</span>}
          </div>
          {draft.isError && (
            <p className="text-sm text-destructive" role="alert">
              {errorText(draft.error)}
            </p>
          )}
          {drafts.length > 0 && (
            <ul className="grid gap-3 md:grid-cols-3" data-testid="ai-drafts">
              {drafts.map((d) => (
                <li key={d.style} className="rounded-md border border-border p-2">
                  <p className="text-xs font-semibold text-muted-foreground">{d.style}</p>
                  <p className="mt-1 whitespace-pre-wrap text-sm">{d.caption}</p>
                  <Button className="mt-2" type="button" size="sm" onClick={() => setWorking(d.caption)}>
                    Use this one
                  </Button>
                </li>
              ))}
            </ul>
          )}

          <label className="block text-sm">
            Caption
            <textarea
              aria-label="Working caption"
              className="mt-1 w-full rounded-md border border-input bg-background p-2 text-sm"
              rows={8}
              value={working}
              onChange={(e) => setWorking(e.target.value)}
            />
          </label>
          {working.trim() !== '' && (
            <div className="flex flex-wrap items-center gap-2">
              <input
                className="min-w-[14rem] flex-1 rounded-md border border-input bg-background p-2 text-sm"
                placeholder="Change it: punchier, shorter, lead with the offer..."
                value={instruction}
                onChange={(e) => setInstruction(e.target.value)}
              />
              <Button type="button" variant="outline" disabled={refine.isPending || instruction.trim() === ''} onClick={() => refine.mutate()}>
                {refine.isPending ? 'Revising...' : 'Revise caption'}
              </Button>
            </div>
          )}
          {refine.isError && (
            <p className="text-sm text-destructive" role="alert">
              {errorText(refine.error)}
            </p>
          )}

          <div className="flex flex-wrap gap-2 border-t border-border pt-3">
            <Button type="button" onClick={() => onAccept(working)}>
              Use this caption
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </div>
        <aside aria-label="Preview of this caption" data-testid="workspace-preview" className="min-w-0">
          <h3 className="mb-2 text-sm font-semibold">Preview</h3>
          {preview(working)}
        </aside>
      </div>
    </PortalDialog>
  );
}
